import React, { useEffect, useState } from 'react';
import authService from '../../services/authService';

const AdminModels = () => {
  const [providers, setProviders] = useState([]);
  const [editingProvider, setEditingProvider] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [error, setError] = useState('');

  const loadProviders = async () => {
    try {
      const t = authService.getToken();
      const res = await fetch('/api/admin/providers', { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) throw new Error('Failed to load providers');
      const items = await res.json();
      setProviders(Array.isArray(items) ? items : []);
    } catch (e) { setError(e.message || 'Failed to load providers'); }
  };

  const refresh = async () => {
    await loadProviders();
    if (selectedProviderId) {
      await loadModels(selectedProviderId);
    }
  };

  const loadModels = async (providerId) => {
    try {
      const t = authService.getToken();
      const res = await fetch(`/api/admin/providers/${providerId}/models`, { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) throw new Error('Failed to load models');
      const items = await res.json();
      setModels(Array.isArray(items) ? items : []);
    } catch (e) { setError(e.message || 'Failed to load models'); }
  };

  useEffect(() => { loadProviders(); }, []);
  useEffect(() => { if (selectedProviderId) loadModels(selectedProviderId); }, [selectedProviderId]);

  const saveProvider = async () => {
    try {
      const t = authService.getToken();
      const body = { ...editingProvider };
      const res = await fetch('/api/admin/providers', { method: 'POST', headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Save failed (${res.status})`);
      }
      setEditingProvider(null);
      try { await loadProviders(); } catch {}
    } catch (e) { setError(e.message || 'Save failed'); }
  };

  const saveModel = async () => {
    try {
      const t = authService.getToken();
      const body = { ...editingModel };
      const res = await fetch(`/api/admin/providers/${selectedProviderId}/models`, { method: 'POST', headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Save failed (${res.status})`);
      }
      setEditingModel(null);
      try { await loadModels(selectedProviderId); } catch {}
    } catch (e) { setError(e.message || 'Save failed'); }
  };

  return (
    <div className="admin-section">
      <h2 className="admin-header">Model Management</h2>
      {error && <div className="error-message" style={{marginBottom:8}}>{error}</div>}

      <div className="admin-controls" style={{ gap: 8 }}>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <label style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Provider</span>
            <select 
              value={selectedProviderId || ''} 
              onChange={e => setSelectedProviderId(e.target.value ? Number(e.target.value) : null)}
              style={{
                padding: '8px 12px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                background: '#fff',
                cursor: 'pointer',
                minWidth: '150px'
              }}
              onFocus={(e)=>{
                e.target.style.borderColor = '#667eea';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e)=>{
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="">Choose…</option>
              {providers.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          </label>
          <button 
            onClick={() => setEditingProvider({ id: 0, key: '', name: '', baseUrl: '', defaultModel: '', isActive: true })}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
            }}
            onMouseEnter={(e)=>{
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
            onMouseLeave={(e)=>{
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
            }}
          >
            Add Provider
          </button>
          {selectedProviderId && (
            <button 
              onClick={() => setEditingModel({ id: 0, modelName: '', isAllowed: true, sortOrder: (models[models.length-1]?.sortOrder || 0) + 10 })}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
              }}
              onMouseEnter={(e)=>{
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
              }}
              onMouseLeave={(e)=>{
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
              }}
            >
              Add Model
            </button>
          )}
          <button 
            onClick={refresh}
            style={{
              padding: '10px 20px',
              background: '#fff',
              color: '#667eea',
              border: '1.5px solid #667eea',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e)=>{
              e.target.style.background = '#667eea';
              e.target.style.color = '#fff';
            }}
            onMouseLeave={(e)=>{
              e.target.style.background = '#fff';
              e.target.style.color = '#667eea';
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="table-scroll">
        <table className="admin-table">
          <thead><tr><th>Provider</th><th>Key</th><th>Base URL</th><th>Default Model</th><th>Active</th><th>Actions</th></tr></thead>
          <tbody>
            {providers.map(p => (
              <tr key={p.id} className={selectedProviderId === p.id ? 'active-row' : ''}>
                <td>{p.name}</td><td>{p.key}</td><td>{p.baseUrl||''}</td><td>{p.defaultModel||''}</td><td>{p.isActive?'Yes':'No'}</td>
                <td>
                  <button 
                    onClick={() => { setSelectedProviderId(p.id); }}
                    style={{
                      padding: '6px 12px',
                      background: '#fff',
                      color: '#667eea',
                      border: '1.5px solid #667eea',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e)=>{
                      e.target.style.background = '#667eea';
                      e.target.style.color = '#fff';
                    }}
                    onMouseLeave={(e)=>{
                      e.target.style.background = '#fff';
                      e.target.style.color = '#667eea';
                    }}
                  >
                    Select
                  </button>
                  <button 
                    onClick={() => setEditingProvider(p)}
                    style={{
                      marginLeft: 6,
                      padding: '6px 12px',
                      background: '#fff',
                      color: '#667eea',
                      border: '1.5px solid #667eea',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e)=>{
                      e.target.style.background = '#667eea';
                      e.target.style.color = '#fff';
                    }}
                    onMouseLeave={(e)=>{
                      e.target.style.background = '#fff';
                      e.target.style.color = '#667eea';
                    }}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedProviderId && (
        <div className="table-scroll" style={{ marginTop: 12 }}>
          <table className="admin-table">
            <thead><tr><th>Model Name</th><th>Allowed</th><th>Sort</th><th>Actions</th></tr></thead>
            <tbody>
              {models.map(m => (
                <tr key={m.id}>
                  <td>{m.modelName}</td>
                  <td>{m.isAllowed ? 'Yes' : 'No'}</td>
                  <td>{m.sortOrder}</td>
                  <td>
                    <button 
                      onClick={() => setEditingModel(m)}
                      style={{
                        padding: '6px 12px',
                        background: '#fff',
                        color: '#667eea',
                        border: '1.5px solid #667eea',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e)=>{
                        e.target.style.background = '#667eea';
                        e.target.style.color = '#fff';
                      }}
                      onMouseLeave={(e)=>{
                        e.target.style.background = '#fff';
                        e.target.style.color = '#667eea';
                      }}
                    >
                      Edit
                    </button>
                    <button 
                      onClick={async()=>{
                        const t = authService.getToken();
                        const res = await fetch(`/api/admin/providers/${selectedProviderId}/models/${m.id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${t}` } });
                        if (res.ok) await loadModels(selectedProviderId);
                      }}
                      style={{
                        marginLeft:6,
                        padding: '6px 12px',
                        background: '#fff',
                        color: '#dc2626',
                        border: '1.5px solid #dc2626',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e)=>{
                        e.target.style.background = '#dc2626';
                        e.target.style.color = '#fff';
                      }}
                      onMouseLeave={(e)=>{
                        e.target.style.background = '#fff';
                        e.target.style.color = '#dc2626';
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingProvider && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ width: 'min(520px, 92vw)' }}>
            <button className="modal-close" onClick={() => setEditingProvider(null)}>×</button>
            <h3 style={{ marginTop: 0 }}>{editingProvider.id ? 'Edit Provider' : 'Add Provider'}</h3>
            <div className="form-vertical">
              <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Key</span>
                <input 
                  value={editingProvider.key} 
                  onChange={e => setEditingProvider({ ...editingProvider, key: e.target.value })}
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    background: '#fff',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e)=>{
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e)=>{
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
              <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Name</span>
                <input 
                  value={editingProvider.name} 
                  onChange={e => setEditingProvider({ ...editingProvider, name: e.target.value })}
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    background: '#fff',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e)=>{
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e)=>{
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
              <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Base URL</span>
                <input 
                  value={editingProvider.baseUrl||''} 
                  onChange={e => setEditingProvider({ ...editingProvider, baseUrl: e.target.value })}
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    background: '#fff',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e)=>{
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e)=>{
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
              <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Default Model</span>
                <input 
                  value={editingProvider.defaultModel||''} 
                  onChange={e => setEditingProvider({ ...editingProvider, defaultModel: e.target.value })}
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    background: '#fff',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e)=>{
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e)=>{
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:6, marginTop:8 }}>
                <input type="checkbox" checked={!!editingProvider.isActive} onChange={e => setEditingProvider({ ...editingProvider, isActive: e.target.checked })} />
                <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Active</span>
              </label>
            </div>
            <div className="modal-actions" style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
              <button 
                onClick={() => setEditingProvider(null)}
                style={{
                  padding: '10px 20px',
                  background: '#fff',
                  color: '#6b7280',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e)=>{
                  e.target.style.background = '#f3f4f6';
                  e.target.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e)=>{
                  e.target.style.background = '#fff';
                  e.target.style.borderColor = '#e5e7eb';
                }}
              >
                Cancel
              </button>
              <button 
                onClick={saveProvider}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
                }}
                onMouseEnter={(e)=>{
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
                onMouseLeave={(e)=>{
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {editingModel && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ width: 'min(420px, 92vw)' }}>
            <button className="modal-close" onClick={() => setEditingModel(null)}>×</button>
            <h3 style={{ marginTop: 0 }}>{editingModel.id ? 'Edit Model' : 'Add Model'}</h3>
            <div className="form-vertical">
              <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Model Name</span>
                <input 
                  value={editingModel.modelName} 
                  onChange={e => setEditingModel({ ...editingModel, modelName: e.target.value })}
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    background: '#fff',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e)=>{
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e)=>{
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:6, marginTop:8 }}>
                <input type="checkbox" checked={!!editingModel.isAllowed} onChange={e => setEditingModel({ ...editingModel, isAllowed: e.target.checked })} />
                <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Allowed</span>
              </label>
              <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Sort Order</span>
                <input 
                  type="number" 
                  value={editingModel.sortOrder||0} 
                  onChange={e => setEditingModel({ ...editingModel, sortOrder: Number(e.target.value) })}
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    background: '#fff',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e)=>{
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e)=>{
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
            </div>
            <div className="modal-actions" style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
              <button 
                onClick={() => setEditingModel(null)}
                style={{
                  padding: '10px 20px',
                  background: '#fff',
                  color: '#6b7280',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e)=>{
                  e.target.style.background = '#f3f4f6';
                  e.target.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e)=>{
                  e.target.style.background = '#fff';
                  e.target.style.borderColor = '#e5e7eb';
                }}
              >
                Cancel
              </button>
              <button 
                onClick={saveModel}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
                }}
                onMouseEnter={(e)=>{
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
                onMouseLeave={(e)=>{
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminModels;


